import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import type { ReactElement, ReactNode } from 'react';

import { cn } from '@core/utils/cn';

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
}

/**
 * Wraps a single trigger element. Base UI owns the positioning, collision handling and
 * the shared open/close timing between adjacent tooltips -- the things that are tedious
 * to get right and invisible when they are.
 *
 * Open delay is intentionally NOT a prop here. In Base UI 1.8 `delay` lives on
 * `Tooltip.Provider`, not on `Root`, which is the better design anyway: one shared delay
 * for the whole app means sweeping a pointer across a toolbar shows the second tooltip
 * instantly instead of re-waiting. Mount {@link TooltipProvider} once near the root.
 */
export function Tooltip({ content, children, side = 'top', className }: TooltipProps) {
  return (
    <BaseTooltip.Root>
      <BaseTooltip.Trigger render={children} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner side={side} sideOffset={8} className="z-50">
          <BaseTooltip.Popup
            className={cn(
              'bg-fg text-bg shadow-e2 max-w-xs rounded-md px-2.5 py-1.5 text-[12px] font-medium',
              // Transitions rather than animations: a transition can be cancelled
              // cleanly mid-flight, which matters when a pointer sweeps across a toolbar.
              'ease-out-quart origin-(--transform-origin) transition-[transform,opacity] duration-150',
              'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
              'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
              'motion-reduce:transition-none',
              className,
            )}
          >
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}

/**
 * Mount once near the root. `timeout` is the grace period in which an adjacent tooltip
 * opens instantly rather than waiting out `delay` again.
 */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <BaseTooltip.Provider delay={400} closeDelay={100} timeout={400}>
      {children}
    </BaseTooltip.Provider>
  );
}
